import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProgressIndicator from './ProgressIndicator';
import css from './style.module.css';
import AddMarktingForm from './AddMarktingForm';
import DocumentComments from './DocumentComments';
import Header from './Header';

import { MarktingContext } from '../../Context/MarktingContext';
import LoadingMarketingDetails from '../LoadingPages/LoadingMarketingDetails';
import HTMLViewer from './HTMLViewer';

import ImageComponent from './ImageComponent';
import ConvertedHtml from './ConvertedHtml';
import { useOktaConfig } from '../../Context/CredentialsContext';
import FileDetails from './FileDetails';
import BackSection from '../Services/BackSection';
import Attestation from '../Services/Attestation';

export default function MarketingDetails() {
    const { fetchDataById, reviewData } = useContext(MarktingContext);
    const { loginToken } = useOktaConfig();
    const { fileId } = useParams();
    const [comments, setComments] = useState([]);
    const [id, setId] = useState(null);
    const [docInfo, setDocInfo] = useState({ documentUrl: [] });
    const [animationPlayed, setAnimationPlayed] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [archived, setArchived] = useState(false);
    //   the active comment (ref. to the currently )
    const [activeComment, setActiveComment] = useState(null);
    // New state to track active comment from feed for image highlighting
    const [activeCommentFromFeed, setActiveCommentFromFeed] = useState(null);
    // progress indicator file status
    const [filestatus, setFileStatus] = useState(null);
    const [convertedHtml, setConvertedHtml] = useState(false);
    const [documentStatus, setDocumentStatus] = useState(null);
    const [appear, setAppear] = useState(false);
    const documentUrl = typeof currentVersion === "string" ? currentVersion : "";
    const isImage = /\.(jpeg|jpg|gif|png|bmp)/i.test(documentUrl);
    const isHtml = [".html", ".htm", "HTML"].some(ext => documentUrl.includes(ext));

    // Handle eye icon click from DocumentComments
    const handleEyeIconClick = (comment) => {
        setActiveCommentFromFeed(comment);

        // Clear the highlight after a delay
        setTimeout(() => {
            setActiveCommentFromFeed(null);
        }, 4000);
    };

    useEffect(() => {
        async function fetchData() {
            if (fileId) {
                const document = await fetchDataById(fileId);

                if (document?.data?.recentlyUpdated) {
                    reviewData(document?.data.documentId);
                }

                setDocInfo(document?.data);
                setId(fileId);
                setConvertedHtml(document?.documentConvertedBefore);

                setCurrentVersion(document?.documentUrl);
                setDocumentStatus(document?.data?.status);
                setArchived(document?.data?.archived);
            }
        }
        fetchData();
    }, [fileId]);

    if (!currentVersion) {
        return <LoadingMarketingDetails />;
    }

    const targetPath = "/marketingcompliance";
    const label = null;
    const header = "Document Submission Confirmation";
    const description = "Thank you! Your document has been submitted for review.";

    return (
        <section>
            <BackSection pageName="Documents" to={"/marketingcompliance"} />
            <div className="mt-3">
                <Header archived={archived} setArchived={setArchived}
                    loginToken={loginToken} setId={setId} id={id} documentStatus={documentStatus ?? documentStatus}
                    setDocumentStatus={setDocumentStatus} setDocInfo={setDocInfo} animationPlayed={animationPlayed} setAnimationPlayed={setAnimationPlayed}
                    setCurrentVersion={setCurrentVersion} setConvertedHtml={setConvertedHtml} filestatus={filestatus} setFileStatus={setFileStatus}
                    setComments={setComments} />

                <AddMarktingForm setAppear={setAppear} setDocumentStatus={setDocumentStatus} loginToken={loginToken} setDocInfo={setDocInfo} animationPlayed={animationPlayed} setAnimationPlayed={setAnimationPlayed} marketingDocId={docInfo?.documentId} />
                {appear ? <Attestation setAppear={setAppear} targetPath={targetPath} label={label} header={header} description={description} /> : ""}
                <FileDetails createdBy={docInfo?.created_by} ReviewedBy={docInfo?.approved_by} dataAssigned={docInfo?.createdAt} documentStatus={documentStatus ?? documentStatus} firstVersion={docInfo} />
                <ProgressIndicator documentStatus={documentStatus ?? documentStatus} setDocumentStatus={setDocumentStatus} filestatus={filestatus} setFileStatus={setFileStatus} />
                <div className="row d-flex">
                    <div className="col-md-9 vh-100">
                        {isImage ? (
                            currentVersion && (
                                <>
                                    <ImageComponent
                                        setDocumentStatus={setDocumentStatus}
                                        archived={archived ?? archived}
                                        loginToken={loginToken}
                                        imgUrl={currentVersion}
                                        setActiveComment={setActiveComment}
                                        setComments={setComments}
                                        comments={comments}
                                        id={id}
                                        activeCommentFromFeed={activeCommentFromFeed} // Pass the active comment from feed
                                    />
                                </>
                            )
                        ) :
                            isHtml ? (
                                !convertedHtml ? <HTMLViewer setDocumentStatus={setDocumentStatus}
                                    archived={archived ?? archived}
                                    loginToken={loginToken}
                                    setActiveComment={setActiveComment}
                                    setComments={setComments}
                                    comments={comments} id={id} activeCommentFromFeed={activeCommentFromFeed} /> :
                                    <ConvertedHtml setDocumentStatus={setDocumentStatus}
                                        documentStatus={documentStatus ?? documentStatus}
                                        currentVersion={currentVersion ?? currentVersion}
                                        archived={archived ?? archived}
                                        loginToken={loginToken}
                                        id={id} comments={comments}
                                        setComments={setComments}
                                        setActiveComment={setActiveComment}
                                        activeCommentFromFeed={activeCommentFromFeed} />
                            ) :
                                (
                                    <iframe
                                        src={currentVersion ?? currentVersion}
                                        width="100%"
                                        height="100%"
                                        title="File Viewer"
                                        className="rounded-4 px-1 py-2"
                                    />
                                )}
                    </div>
                    <div className="col-md-3">
                        <DocumentComments
                            archived={archived ?? archived}
                            loginToken={loginToken}
                            setComments={setComments}
                            comments={comments}
                            id={id}
                            activeComment={activeComment}
                            onEyeIconClick={handleEyeIconClick} // Pass the eye icon click handler
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}